/**
 * The caller's effective permissions, derived once from the `/me` role grants.
 *
 * This mirrors the API's *Effective permissions* table and its scope
 * resolution (department → company → global, with `admin` passing
 * everything). It is a courtesy for the user — the UI hides what a call would
 * only refuse — never a security boundary: every write path still handles a
 * 403 arriving anyway.
 */

import type { RoleGrant } from '@/api/types';

/** What a department-scoped check needs to know about its ancestry. */
export interface DepartmentScope {
  id: string;
  company_id: string | null;
}

export interface Permissions {
  isAdmin: boolean;
  /** Companies the caller holds company_admin on (empty for admin — they hold all). */
  administeredCompanyIds: readonly string[];
  /** Departments the caller holds department_admin on. */
  administeredDepartmentIds: readonly string[];
  /** Any administrative authority at all — drives whether admin UI appears. */
  hasAnyAdminRole: boolean;

  // Company scope
  canCreateCompany: boolean;
  canDeleteCompany: boolean;
  canUpdateCompany(companyId: string): boolean;
  /** Domains, settings, member list — company internals beyond the name. */
  canViewCompanyInternals(companyId: string): boolean;
  canUpdateCompanyDomains: boolean;
  canUpdateCompanySettings: boolean;
  canManageCompanyUsers(companyId: string): boolean;

  // Department scope
  canManageDepartments(companyId: string): boolean;
  canUpdateDepartment(department: DepartmentScope): boolean;
  canViewDepartmentMembers(department: DepartmentScope): boolean;
  /**
   * Whether a department is worth offering as a dashboard-panel target. A
   * courtesy filter only — mirrors the API's own exemption, where
   * `departments.users.stats.view` never inherits from `company_admin`
   * (`php/api_auth.php`'s `hasAccess()`), so a company admin who is not also
   * the department's coach does not see it in the picker even though they
   * administer other things about it.
   */
  canViewDepartmentStats(department: DepartmentScope): boolean;
  canSetVisibility: boolean;
  canRecordCollectiveConsent: boolean;

  // Roles and people
  canGrantRoles: boolean;
  canProvisionInCompany(companyId: string): boolean;
  canProvisionInDepartment(department: DepartmentScope): boolean;
  /** A department admin never changes an existing address. */
  canChangeEmail(companyId: string | null): boolean;
  canLinkIdentities: boolean;
  canErase: boolean;

  // Audit
  canViewAudit: boolean;
  canViewFullAudit: boolean;

  /**
   * System › API requests (`GET /requests`, § Beheer): the ingest API's
   * request log per `source_id`. Platform diagnostics — admin only, never a
   * company or department admin (see `src/manage/system/RequestsPage.tsx`
   * for the API-side prerequisite this assumes).
   */
  canViewApiRequests: boolean;

  // Navigation and Manage — see PLAN-FASE1.md § Zichtbaarheid per functionaliteit
  /**
   * The Departments section in the main menu: admin, company admin and
   * department admin/coach (columns A/C/D). A plain department member or
   * company member without a role (M/B) gets no Departments heading at all.
   */
  canSeeDepartmentsSection: boolean;
  /**
   * The Users section: the same `departments.users.view`-holders as above
   * (A/C/D) — a plain member never sees a member list, only a department
   * admin, company admin or platform admin does.
   */
  canSeeUsersSection: boolean;
  /**
   * The Programs pill's Challenges subitem (PLAN-FASE2.md WP-05). Mirrors
   * the API: `challenges.*` is checked at company scope only
   * (`php/api_auth.php`'s `hasAccess()` has no department-to-company
   * inheritance), so a department admin/coach alone gets no Challenges
   * access, unlike Departments/Users above — admin or company admin only.
   */
  canSeeChallengesSection: boolean;
  /**
   * The Content pill's Communicatie subitem (PLAN-FASE3.md WP-02). Mirrors
   * the API: `companies.communications.*` is checked at company scope only
   * (same shape as `companies.locations.*`), so admin or company admin
   * only — a department admin/coach alone gets no access, same as
   * Challenges above.
   */
  canSeeCommunicationsSection: boolean;
  /**
   * The Content pill's Documenten subitem (PLAN-FASE3.md WP-03). Mirrors
   * the API: `companies.documents.*` (admin write/list, not the self-auth
   * read confirmation) is checked at company scope only, same shape as
   * Communicatie above — admin or company admin only.
   */
  canSeeDocumentsSection: boolean;
  /**
   * The Content pill's Rapportages subitem (PLAN-FASE3.md WP-04). Mirrors
   * the API: `companies.reports.view` is checked at company scope only,
   * same shape as Communicatie/Documenten above — admin or company admin
   * only.
   */
  canSeeReportsSection: boolean;
  /**
   * The main menu's Instellingen item (moved out of the account menu into
   * the flat sidebar list, § Hoofdmenu redesign). Mirrors the account
   * menu's own gate, `canViewCompanyInternals(activeCompanyId)`, loosened
   * to not need a specific company id — same admin/company-admin-only
   * shape as Challenges/Communicatie/Documenten/Rapportages above.
   */
  canSeeSettingsSection: boolean;
  /** The Manage button/`/manage`: platform admin or company admin only — never a department admin alone. */
  canManage: boolean;
  /** Setting a user/company/department's edition — admin-only. */
  canSetEdition: boolean;
}

export function derivePermissions(grants: readonly RoleGrant[]): Permissions {
  const isAdmin = grants.some((g) => g.role === 'admin' && g.scope_type === 'global');
  const companyIds = grants
    .filter((g) => g.role === 'company_admin' && g.scope_id !== null)
    .map((g) => g.scope_id as string);
  const departmentIds = grants
    .filter((g) => g.role === 'department_admin' && g.scope_id !== null)
    .map((g) => g.scope_id as string);

  const overCompany = (companyId: string) => isAdmin || companyIds.includes(companyId);
  const overDepartment = (department: DepartmentScope) =>
    isAdmin ||
    departmentIds.includes(department.id) ||
    (department.company_id !== null && companyIds.includes(department.company_id));

  return {
    isAdmin,
    administeredCompanyIds: companyIds,
    administeredDepartmentIds: departmentIds,
    hasAnyAdminRole: isAdmin || companyIds.length > 0 || departmentIds.length > 0,

    canCreateCompany: isAdmin,
    canDeleteCompany: isAdmin,
    canUpdateCompany: overCompany,
    canViewCompanyInternals: overCompany,
    canUpdateCompanyDomains: isAdmin,
    canUpdateCompanySettings: isAdmin,
    canManageCompanyUsers: overCompany,

    canManageDepartments: overCompany,
    canUpdateDepartment: overDepartment,
    canViewDepartmentMembers: overDepartment,
    canViewDepartmentStats: (department) => isAdmin || departmentIds.includes(department.id),
    canSetVisibility: isAdmin,
    canRecordCollectiveConsent: isAdmin,

    canGrantRoles: isAdmin,
    canProvisionInCompany: overCompany,
    canProvisionInDepartment: overDepartment,
    canChangeEmail: (companyId) =>
      isAdmin || (companyId !== null && companyIds.includes(companyId)),
    canLinkIdentities: isAdmin || companyIds.length > 0 || departmentIds.length > 0,
    canErase: isAdmin,

    canViewAudit: isAdmin || companyIds.length > 0,
    canViewFullAudit: isAdmin,
    canViewApiRequests: isAdmin,

    canSeeDepartmentsSection: isAdmin || companyIds.length > 0 || departmentIds.length > 0,
    canSeeUsersSection: isAdmin || companyIds.length > 0 || departmentIds.length > 0,
    canSeeChallengesSection: isAdmin || companyIds.length > 0,
    canSeeCommunicationsSection: isAdmin || companyIds.length > 0,
    canSeeDocumentsSection: isAdmin || companyIds.length > 0,
    canSeeSettingsSection: isAdmin || companyIds.length > 0,
    canSeeReportsSection: isAdmin || companyIds.length > 0,
    canManage: isAdmin || companyIds.length > 0,
    canSetEdition: isAdmin,
  };
}

/**
 * Narrows a list of departments (already scoped to the active company by the
 * caller — e.g. `GET /companies/{id}/departments`) to the ones this caller
 * may act on. An admin or the active company's own admin sees all of them; a
 * department admin/coach sees only the department(s) they hold a grant on
 * ("D ziet zijn afdeling(en) direct, geen 'alle afdelingen'" — see the
 * matrix). Anyone else — this function is never reached for them, since
 * `canSeeDepartmentsSection` is already false — gets nothing.
 */
export function visibleDepartments<T extends DepartmentScope>(
  permissions: Permissions,
  companyId: string | null,
  departments: readonly T[],
): T[] {
  if (permissions.isAdmin || (companyId !== null && permissions.canUpdateCompany(companyId))) {
    return [...departments];
  }
  return departments.filter((department) =>
    permissions.administeredDepartmentIds.includes(department.id),
  );
}
